/* S1 · SKIPPY — chat core (default screen for Nick). The forced-response pipeline
 * (§5) lives in the engine; this surface only renders turns + their receipts strip. */
import { getStore, BridgeError } from '../store';
import type { ChatMessage } from '../store/types';
import { esc, surfaceTitle } from '../components/ui';
import { icon } from '../components/icons';

let lane: 'DEEP' | 'FAST' = 'DEEP';

function msgHtml(m: ChatMessage): string {
  const receipts = m.receipts?.length
    ? `<div class="receipts">${m.receipts
        .map(
          (r) =>
            `<span class="chip ${r.claimClass === 'qualitative' ? 'inference' : ''}">${esc(r.label)}${
              r.claimClass === 'qualitative' ? ' · model inference' : ''
            }</span>`,
        )
        .join('')}</div>`
    : '';
  return `<div class="msg ${m.role}">${esc(m.text)}${receipts}</div>`;
}

export async function renderSkippy(root: HTMLElement): Promise<void> {
  const store = getStore();
  root.innerHTML =
    surfaceTitle('Skippy') +
    `<div class="chatlog" id="chatlog"></div>
     <div class="composer">
       <button class="btn ghost" id="lane" title="Toggle depth">${lane}</button>
       <input id="chatin" placeholder="Ask Skippy…" autocomplete="off" />
       <button class="btn" id="mic" title="Hold to talk">${icon('mic')}</button>
       <button class="btn" id="send">Send</button>
     </div>`;

  const log = root.querySelector<HTMLDivElement>('#chatlog')!;
  const input = root.querySelector<HTMLInputElement>('#chatin')!;
  const laneBtn = root.querySelector<HTMLButtonElement>('#lane')!;

  const paint = (msgs: ChatMessage[]) => {
    log.innerHTML = msgs.map(msgHtml).join('');
    log.scrollTop = log.scrollHeight;
  };
  paint(await store.getChatHistory());

  laneBtn.onclick = () => {
    lane = lane === 'DEEP' ? 'FAST' : 'DEEP';
    laneBtn.textContent = lane;
  };

  const submit = async () => {
    const text = input.value.trim();
    if (!text) return;
    input.value = '';
    // optimistic user echo
    paint([...(await store.getChatHistory())]);
    try {
      await store.sendChat(text, lane);
      paint(await store.getChatHistory());
    } catch (e) {
      // Never fabricate a Skippy reply on failure — show an honest system note.
      const msg =
        e instanceof BridgeError && e.retryable
          ? 'Skippy is busy right now (engine overloaded) — tap Send again in a moment.'
          : e instanceof BridgeError && e.kind === 'network'
            ? "Can't reach the engine — is the bridge running?"
            : `Something went wrong: ${esc(String((e as Error)?.message ?? e))}`;
      log.insertAdjacentHTML('beforeend', `<div class="msg system"><span class="softfail">${msg}</span></div>`);
      log.scrollTop = log.scrollHeight;
    }
  };

  root.querySelector<HTMLButtonElement>('#send')!.onclick = submit;
  input.onkeydown = (e) => {
    if (e.key === 'Enter') submit();
  };
}
